import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from "@angular/material/autocomplete";
import { Observable, Subject, Subscription, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from "rxjs/operators";
import { EmployeesService } from "../../employees/employees.service";
import { EmployeePickerItem } from "../../employees/employees.interface";
import type { PickerQuery } from "../digital-cards.interface";

const SEARCH_DEBOUNCE_MS = 280;
const SEARCH_PAGE_SIZE = 50;

@Component({
  selector: "app-digital-card-employee-picker",
  templateUrl: "./digital-card-employee-picker.component.html",
  styleUrls: ["./digital-card-employee-picker.component.scss"],
  standalone: false,
})
export class DigitalCardEmployeePickerComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() disabled = false;
  @Input() templateContextId: string | null = null;
  @Input() preferredUserId: string | null = null;
  @Input()
  set value(v: EmployeePickerItem | null) {
    this.selectedEmployee = v ?? null;
    this.searchCtrl.setValue(this.displayLabel(v), { emitEvent: false });
    if (v && !this.firstFetchHandled) {
      this.markInitialResolved();
    }
  }
  @Output() valueChange = new EventEmitter<EmployeePickerItem | null>();
  @Output() initialSelectionResolved = new EventEmitter<void>();

  @ViewChild(MatAutocompleteTrigger) autocomplete?: MatAutocompleteTrigger;

  searchCtrl = new FormControl<string>("", { nonNullable: true });
  options: EmployeePickerItem[] = [];
  selectedEmployee: EmployeePickerItem | null = null;
  selectionReady = false;

  private readonly query$ = new Subject<PickerQuery>();
  private readonly sub = new Subscription();
  private firstFetchHandled = false;
  private refreshSeq = 0;

  constructor(private employeesService: EmployeesService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes["templateContextId"] ||
      changes["templateContextId"].firstChange
    ) {
      return;
    }
    const prev =
      (changes["templateContextId"].previousValue as string | null)?.trim() ||
      null;
    const next = (this.templateContextId ?? "").trim() || null;
    if (prev === next) {
      return;
    }
    this.firstFetchHandled = false;
    this.selectionReady = false;
    this.selectedEmployee = null;
    this.searchCtrl.setValue("", { emitEvent: false });
    this.options = [];
    this.refreshSeq += 1;
    this.query$.next({
      phrase: undefined,
      refreshSeq: this.refreshSeq,
    });
  }

  ngOnInit(): void {
    this.sub.add(
      this.query$
        .pipe(
          distinctUntilChanged(
            (a, b) => a.phrase === b.phrase && a.refreshSeq === b.refreshSeq,
          ),
          switchMap((q) => this.fetchEmployees(q.phrase)),
          tap((rows) => this.applyDefaultSelection(rows)),
        )
        .subscribe((rows) => {
          this.options = rows;
        }),
    );
    this.sub.add(
      this.searchCtrl.valueChanges
        .pipe(debounceTime(SEARCH_DEBOUNCE_MS))
        .subscribe((v) =>
          this.query$.next({
            phrase: this.queryFromInput(v),
            refreshSeq: this.refreshSeq,
          }),
        ),
    );
    const preferredUserId = (this.preferredUserId ?? "").trim();
    if (preferredUserId) {
      this.sub.add(
        this.employeesService
          .getEmployee(preferredUserId)
          .pipe(
            map((res): EmployeePickerItem => ({
              employeeId: res.employeeId,
              email: res.email ?? "",
              fullName: res.fullName,
            })),
            catchError(() => of(null)),
          )
          .subscribe((row) => {
            if (row?.employeeId) {
              this.selectEmployee(row);
              this.markInitialResolved();
            }
            this.query$.next({
              phrase: undefined,
              refreshSeq: this.refreshSeq,
            });
          }),
      );
      return;
    }
    this.query$.next({
      phrase: undefined,
      refreshSeq: this.refreshSeq,
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  fullName(e: EmployeePickerItem): string {
    return (
      e.fullName?.trim() ||
      `${e.givenName ?? ""} ${e.familyName ?? ""}`.trim() ||
      ""
    );
  }

  email(e: EmployeePickerItem): string {
    return e.email?.trim() || "";
  }

  displayLabel(e: EmployeePickerItem | null): string {
    if (!e) return "";
    const name = this.fullName(e);
    const mail = this.email(e);
    if (name && mail) return `${name} (${mail})`;
    return mail || name;
  }

  optionTrackBy = (_: number, item: EmployeePickerItem): string =>
    item.employeeId;

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectEmployee(event.option.value as EmployeePickerItem);
  }

  onTriggerClick(): void {
    if (this.disabled) return;
    if (this.searchCtrl.value && this.selectedEmployee) {
      this.searchCtrl.setValue("", { emitEvent: true });
    }
    this.autocomplete?.openPanel();
  }

  onBlur(): void {
    if (!this.selectedEmployee) {
      return;
    }
    const expected = this.displayLabel(this.selectedEmployee);
    if (this.searchCtrl.value?.trim() !== expected) {
      this.searchCtrl.setValue(expected, { emitEvent: false });
    }
  }

  private selectEmployee(row: EmployeePickerItem): void {
    this.selectedEmployee = row;
    this.searchCtrl.setValue(this.displayLabel(row), { emitEvent: false });
    this.valueChange.emit(row);
  }

  private markInitialResolved(): void {
    if (this.firstFetchHandled) {
      return;
    }
    this.firstFetchHandled = true;
    this.selectionReady = true;
    this.initialSelectionResolved.emit();
  }

  private applyDefaultSelection(rows: EmployeePickerItem[]): void {
    if (this.selectedEmployee) {
      this.searchCtrl.setValue(this.displayLabel(this.selectedEmployee), {
        emitEvent: false,
      });
      this.valueChange.emit(this.selectedEmployee);
      this.markInitialResolved();
      return;
    }

    const preferredUserId = (this.preferredUserId ?? "").trim();
    if (preferredUserId) {
      const match = rows.find((r) => r.employeeId === preferredUserId);
      if (match) {
        this.selectEmployee(match);
        this.markInitialResolved();
        return;
      }
    }

    if (rows.length > 0) {
      const first = rows.find((r) => this.email(r).length > 0) ?? rows[0];
      this.selectEmployee(first);
      this.markInitialResolved();
      return;
    }

    if (!this.firstFetchHandled) {
      this.valueChange.emit(null);
      this.markInitialResolved();
    }
  }

  private fetchEmployees(
    phrase: string | undefined,
  ): Observable<EmployeePickerItem[]> {
    return this.employeesService
      .getEmployeesPicker({
        pager: { page: 0, limit: SEARCH_PAGE_SIZE },
        phrase,
      })
      .pipe(
        map((res) => (Array.isArray(res.data) ? res.data : [])),
        catchError(() => of([] as EmployeePickerItem[])),
      );
  }

  private queryFromInput(raw: string | null | undefined): string | undefined {
    const v = (raw ?? "").trim();
    if (!v) return undefined;
    if (
      this.selectedEmployee &&
      v === this.displayLabel(this.selectedEmployee)
    ) {
      return undefined;
    }
    return v;
  }
}
